import { ACCESS_TOKEN, REFRESH_TOKEN, USER } from '@/constants/auth';
import axios, {
	AxiosError,
	type AxiosInstance,
	type AxiosRequestConfig,
	type AxiosResponse,
} from 'axios';
import { authService } from '../services';

// ===============================
// CONSTANTS & TYPES
// ===============================
const API_CONFIG = {
	BASE_URL: process.env.NEXT_PUBLIC_API_URL ?? 'https://infora-api-service/v1',
	TIMEOUT: 10000,
} as const;

const HTTP_STATUS = {
	UNAUTHORIZED: 401,
	FORBIDDEN: 403,
} as const;

interface QueuedRequest {
	resolve: (value: string) => void;
	reject: (error: any) => void;
}

interface ApiClientConfig {
	baseURL?: string;
	timeout?: number;
}

// Extend AxiosRequestConfig to include _retry property
declare module 'axios' {
	interface InternalAxiosRequestConfig {
		_retry?: boolean;
	}
}

// ===============================
// API CLIENT
// ===============================
export class ApiClient {
	private static instance: ApiClient;
	public readonly axiosInstance: AxiosInstance;

	// Refresh state management
	private isRefreshing = false;
	private refreshAttempted = false;
	private refreshPromise: Promise<string> | null = null;
	private requestQueue: QueuedRequest[] = [];

	private constructor(config: ApiClientConfig = {}) {
		this.axiosInstance = this.createAxiosInstance(config);
		this.setupInterceptors();
	}

	public static getInstance(config?: ApiClientConfig): ApiClient {
		if (!ApiClient.instance) {
			ApiClient.instance = new ApiClient(config);
		}
		return ApiClient.instance;
	}

	/**
	 * Create axios instance with configuration
	 */
	private createAxiosInstance(config: ApiClientConfig): AxiosInstance {
		return axios.create({
			baseURL: config.baseURL ?? API_CONFIG.BASE_URL,
			timeout: config.timeout ?? API_CONFIG.TIMEOUT,
			headers: {
				'Content-Type': 'application/json',
			},
		});
	}

	/**
	 * Setup request and response interceptors
	 */
	private setupInterceptors(): void {
		// Request interceptor - add auth header (step 1-2 in diagram)
		this.axiosInstance.interceptors.request.use(
			(config) => {
				const token = localStorage.getItem(ACCESS_TOKEN);
				if (token) {
					config.headers.Authorization = `Bearer ${token}`;
				}
				return config;
			},
			(error) => Promise.reject(error),
		);

		// Response interceptor - handle 401 errors (step 3-4 in diagram)
		this.axiosInstance.interceptors.response.use(
			(response) => {
				// Reset refresh attempt on successful response
				this.refreshAttempted = false;
				return response;
			},
			async (error: AxiosError) => {
				const originalRequest = error.config;

				// Step 4: Response interceptor catches 401
				if (
					error.response?.status === HTTP_STATUS.UNAUTHORIZED &&
					originalRequest &&
					!originalRequest._retry
				) {
					console.log('🚫 401 Unauthorized - checking refresh status');

					// Step 5: Already attempted refresh?
					if (this.refreshAttempted) {
						console.log('⚠️ Already attempted refresh - immediate logout');
						this.handleLogout(); // Step F: Immediate logout
						return Promise.reject(error);
					}

					originalRequest._retry = true;

					// If refresh in progress, queue the request
					if (this.isRefreshing) {
						console.log('⏳ Refresh in progress - queuing request');
						return this.queueRequest(originalRequest);
					}

					// Step G: Start refresh process
					return this.handleRefresh(originalRequest);
				}

				return Promise.reject(error);
			},
		);
	}

	/**
	 * Queue request during token refresh
	 */
	private queueRequest(originalRequest: any): Promise<AxiosResponse> {
		return new Promise<AxiosResponse>((resolve, reject) => {
			this.requestQueue.push({
				resolve: (token: string) => {
					originalRequest.headers.Authorization = `Bearer ${token}`;
					this.axiosInstance(originalRequest).then(resolve).catch(reject);
				},
				reject,
			});
		});
	}

	/**
	 * Handle token refresh process (step G-N in diagram)
	 */
	private async handleRefresh(originalRequest: any): Promise<AxiosResponse> {
		console.log('🔄 Starting refresh process');

		this.isRefreshing = true;
		this.refreshAttempted = true;

		try {
			// Step H: Call authService.refreshToken() & Step I-J: Get new tokens
			const newToken = await this.refreshToken();

			// Step N: Process queued requests
			this.processQueue(null, newToken);

			// Step M: Retry original request
			originalRequest.headers.Authorization = `Bearer ${newToken}`;
			return this.axiosInstance(originalRequest);
		} catch (refreshError) {
			// Step K-S: Handle refresh failure
			this.processQueue(refreshError, null);

			console.log('💥 Token refresh failed - logging out');
			this.handleLogout(); // Step X: Logout user
			return Promise.reject(refreshError);
		} finally {
			this.isRefreshing = false;
		}
	}

	/**
	 * Refresh access token (step H-K in diagram)
	 */
	private async refreshToken(): Promise<string> {
		if (this.refreshPromise) {
			return this.refreshPromise;
		}

		this.refreshPromise = this.executeRefresh();

		try {
			return await this.refreshPromise;
		} finally {
			this.refreshPromise = null;
		}
	}

	/**
	 * Execute the actual refresh token request (step I-R in diagram)
	 */
	private async executeRefresh(): Promise<string> {
		const refreshToken = localStorage.getItem(REFRESH_TOKEN);
		if (!refreshToken) {
			throw new Error('No refresh token available');
		}

		console.log('🔄 Attempting token refresh...');

		try {
			// Step H: Call authService.refreshToken()
			const response = await authService.refreshToken(refreshToken);
			const { accessToken, refreshToken: newRefreshToken } = response.data;

			// Step L: Save new tokens
			localStorage.setItem(ACCESS_TOKEN, accessToken);
			localStorage.setItem(REFRESH_TOKEN, newRefreshToken);

			console.log('✅ Token refreshed successfully');
			return accessToken;
		} catch (error) {
			console.log('❌ Token refresh failed:', error);

			// Step O-Q: Catch AxiosError and check status
			if (error instanceof AxiosError) {
				const status = error.response?.status;

				// Step Q-R: Status 401 or 403? Both tokens expired
				if (
					status === HTTP_STATUS.UNAUTHORIZED ||
					status === HTTP_STATUS.FORBIDDEN
				) {
					console.log('🔒 Both tokens expired - clearing auth state');
					this.clearAuthState(); // Step T-W: Clear everything
					throw new Error('Both tokens expired');
				}
			}

			// Step S: Other error - will trigger logout in handleRefresh
			throw error;
		}
	}

	/**
	 * Clear authentication state (step T-W in diagram)
	 */
	private clearAuthState(): void {
		console.log('🧹 Clearing authentication state');

		// Step U: Clear localStorage
		localStorage.removeItem(ACCESS_TOKEN);
		localStorage.removeItem(REFRESH_TOKEN);
		localStorage.removeItem(USER);

		// Step V: Reset refresh state
		this.isRefreshing = false;
		this.refreshAttempted = false;
		this.refreshPromise = null;

		// Step W: Clear request queue
		this.processQueue(new Error('Authentication expired'), null);
	}

	/**
	 * Process queued requests after refresh attempt
	 */
	private processQueue(error: any, token: string | null): void {
		for (const { resolve, reject } of this.requestQueue) {
			if (error) {
				reject(error);
			} else {
				resolve(token!);
			}
		}
		this.requestQueue = [];
	}

	/**
	 * Handle logout and cleanup (step X in diagram)
	 */
	private handleLogout(): void {
		console.log('👋 Logging out user...');

		// Clear authentication state
		this.clearAuthState();

		// Redirect to login/reload page
		window.location.reload();
	}

	// ===============================
	// PUBLIC METHODS
	// ===============================

	/**
	 * Reset refresh attempt flag (call after login)
	 */
	public resetRefreshAttempt(): void {
		this.refreshAttempted = false;
	}

	/**
	 * HTTP GET request
	 */
	public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
		const response = await this.axiosInstance.get<T>(url, config);
		return response.data;
	}

	/**
	 * HTTP POST request
	 */
	public async post<T>(
		url: string,
		data?: unknown,
		config?: AxiosRequestConfig,
	): Promise<T> {
		const response = await this.axiosInstance.post<T>(url, data, config);
		return response.data;
	}

	/**
	 * HTTP PUT request
	 */
	public async put<T>(
		url: string,
		data?: unknown,
		config?: AxiosRequestConfig,
	): Promise<T> {
		const response = await this.axiosInstance.put<T>(url, data, config);
		return response.data;
	}

	/**
	 * HTTP DELETE request
	 */
	public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
		const response = await this.axiosInstance.delete<T>(url, config);
		return response.data;
	}

	/**
	 * HTTP PATCH request
	 */
	public async patch<T>(
		url: string,
		data?: unknown,
		config?: AxiosRequestConfig,
	): Promise<T> {
		const response = await this.axiosInstance.patch<T>(url, data, config);
		return response.data;
	}
}

export const apiClient = ApiClient.getInstance();
